/**
 * Keep Scene-tree eye toggles and the Terrain tool card in sync for
 * converter-tagged hillshade / slope / aspect / contours companions.
 *
 * Without this, the tool-card → visibility effect forces derivatives back
 * off whenever the tree eye turns them on (and vice versa looks broken).
 */

import type { Layer } from '@geosoft/viewer-sdk';
import type { TerrainToolCardState } from '@/state/useViewerStore';
import { isTerrainDerivativeLayer } from '@/components/terrain/terrainDerivedLayers';

export function terrainToolCardPatchForLayerVisibility(
  layer: Pick<Layer, 'id' | 'label' | 'style' | 'kind'> | null | undefined,
  visible: boolean,
): Partial<TerrainToolCardState> | null {
  if (!layer || !isTerrainDerivativeLayer(layer)) return null;
  const role =
    typeof layer.style?.['terrainDerivative'] === 'string'
      ? layer.style['terrainDerivative']
      : '';
  switch (role) {
    case 'hillshade':
      return { hillshade: visible };
    case 'slope':
      return { slopeOn: visible };
    case 'aspect':
      return { aspectOn: visible };
    case 'contours':
      return { contoursOn: visible };
    default: {
      // Fallback for artifact-kind spelling without terrainDerivative tag.
      const blob = `${layer.id} ${layer.label ?? ''}`.toLowerCase();
      if (blob.includes('hillshade')) return { hillshade: visible };
      if (blob.includes('slope')) return { slopeOn: visible };
      if (blob.includes('aspect')) return { aspectOn: visible };
      if (blob.includes('contour')) return { contoursOn: visible };
      return null;
    }
  }
}
